import {
  nodeServer,
  getUID,
  getDomainHostname,
} from "./ajax";
import { getNetworkViewLocationId } from "../helpers/networkViewLocation";

const askHeaders = (accept = "text/event-stream") => {
  const headers = {
    Accept: accept,
    "Content-Type": "application/json",
    "domain-hostname": getDomainHostname(),
  };
  const uid = getUID();
  if (uid && uid !== "undefined") headers["x-uid"] = uid;
  const viewLocationId = getNetworkViewLocationId();
  if (viewLocationId) headers["x-view-location-id"] = viewLocationId;
  return headers;
};

const readJson = async (res, fallback) => {
  const err = await res.json().catch(() => ({}));
  const error = new Error(err?.message || fallback);
  error.status = res.status;
  throw error;
};

const askUrl = (path) => {
  const base = String(nodeServer || "").replace(/\/+$/, "");
  return `${base}${path}`;
};

const parseSseBuffer = (buffer, onEvent) => {
  const parts = buffer.split("\n");
  const rest = parts.pop() || "";
  for (const line of parts) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith(":")) continue;
    if (!trimmed.startsWith("data:")) continue;
    const data = trimmed.slice(5).trim();
    if (!data) continue;
    try {
      onEvent(JSON.parse(data));
    } catch (_) {
      /* ignore malformed chunk */
    }
  }
  return rest;
};

export const sendAskMessage = async ({
  message,
  pageContext,
  usePageContext = true,
  conversationId = null,
  lateDays,
  onEvent,
  signal,
}) => {
  const res = await fetch(askUrl("/assist"), {
    method: "POST",
    headers: askHeaders(),
    body: JSON.stringify({
      conversation_id: conversationId || undefined,
      message,
      page_context: usePageContext ? pageContext || undefined : undefined,
      use_page_context: usePageContext !== false,
      late_days: Number(lateDays) > 0 ? Number(lateDays) : undefined,
    }),
    signal,
  });

  const contentType = res.headers.get("content-type") || "";
  if (!res.ok || !contentType.includes("text/event-stream")) {
    const err = await res.json().catch(() => ({}));
    onEvent({
      type: "error",
      code: err?.meta?.code || `http_${res.status}`,
      message: err?.message || "Ask request failed.",
    });
    return;
  }

  if (!res.body) {
    onEvent({ type: "error", code: "empty_stream", message: "Empty stream." });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    buffer = parseSseBuffer(buffer, onEvent);
  }
  buffer += decoder.decode();
  if (buffer.trim()) parseSseBuffer(`${buffer}\n`, onEvent);
};

export const logAskTurn = async ({
  conversationId = null,
  question,
  body,
  kind,
  pageContext,
  answered = true,
} = {}) => {
  const res = await fetch(askUrl("/assist/log"), {
    method: "POST",
    headers: askHeaders("application/json"),
    body: JSON.stringify({
      conversation_id: conversationId || undefined,
      question,
      body,
      kind,
      source: "local",
      page_context: pageContext || undefined,
      answered,
    }),
  });
  if (!res.ok) {
    await readJson(res, "Unable to save Ask turn.");
  }
  const payload = await res.json().catch(() => ({}));
  return payload?.conversation_id || null;
};
